import { useEffect, useMemo, useState } from "react";
import { Download, Plus, SlidersHorizontal, Trash2, X } from "lucide-react";
import { ApiError } from "@/lib/api";
import { Badge, Button, Field, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import { useAuth } from "@/features/auth/auth-context";
import { RequirePermission } from "@/features/auth/RequirePermission";
import { useCustomers } from "@/features/customers/hooks";
import { useSites } from "@/features/sites/hooks";
import { useUsers } from "@/features/users/hooks";
import { useTicketCategories } from "@/features/tickets/hooks";
import { PRIORITY_LABELS, STATUS_LABELS } from "@/features/tickets/labels";
import type { TicketPriority, TicketStatus } from "@/features/tickets/api";
import {
  useBillingRates,
  useCreateBillingRate,
  useDeleteBillingRate,
  useExportReport,
  useReport,
} from "./hooks";
import type { ExportFormat, ReportFilters, ReportName } from "./api";

const BASE_REPORTS: { id: ReportName; label: string }[] = [
  { id: "tickets", label: "Tickets" },
  { id: "calls", label: "Call List" },
  { id: "sla", label: "SLA" },
  { id: "engineers", label: "Engineers" },
  { id: "site-admins", label: "Site Admins" },
  { id: "customers", label: "Customers" },
  { id: "sites", label: "Sites" },
  { id: "amc", label: "AMC" },
];

// Reports whose numbers come from aggregate contract/global data rather than a
// ticket query — the ticket slicers don't apply, so we hide them there.
const NO_TICKET_FILTERS: ReportName[] = ["amc"];

type Filters = {
  start: string;
  end: string;
  site: string;
  customer_id: string;
  engineer_id: string;
  status: string;
  priority: string;
  category_id: string;
};

const EMPTY: Filters = {
  start: "",
  end: "",
  site: "",
  customer_id: "",
  engineer_id: "",
  status: "",
  priority: "",
  category_id: "",
};

function toApiFilters(f: Filters): ReportFilters {
  const out: ReportFilters = {};
  if (f.start) out.start = f.start;
  if (f.end) out.end = f.end;
  if (f.site) out.site = f.site;
  if (f.customer_id) out.customer_id = f.customer_id;
  if (f.engineer_id) out.engineer_id = f.engineer_id;
  if (f.status) out.status = f.status;
  if (f.priority) out.priority = f.priority;
  if (f.category_id) out.category_id = f.category_id;
  return out;
}

/** Column visibility persists per report so a chosen layout survives reloads. */
function colStorageKey(name: ReportName) {
  return `reports.hiddenCols.${name}`;
}

export default function ReportsPage() {
  const { can } = useAuth();
  const canRevenue = can("report:revenue:read");
  const reports = canRevenue
    ? [...BASE_REPORTS, { id: "revenue" as ReportName, label: "Revenue" }]
    : BASE_REPORTS;

  const [name, setName] = useState<ReportName>("tickets");
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [hidden, setHidden] = useState<number[]>([]);
  const exportReport = useExportReport();
  const [exportError, setExportError] = useState<string | null>(null);

  const apiFilters = useMemo(() => toApiFilters(filters), [filters]);
  const { data, isLoading, isError, error } = useReport(name, apiFilters);

  // Load this report's saved column layout whenever the tab changes.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(colStorageKey(name));
      setHidden(raw ? (JSON.parse(raw) as number[]) : []);
    } catch {
      setHidden([]);
    }
  }, [name]);

  function toggleColumn(idx: number) {
    setHidden((prev) => {
      const next = prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx];
      try {
        localStorage.setItem(colStorageKey(name), JSON.stringify(next));
      } catch {
        /* storage full or unavailable — layout just won't persist */
      }
      return next;
    });
  }

  const columns = data?.columns ?? [];
  const visibleCols = columns.map((_, i) => i).filter((i) => !hidden.includes(i));
  const showTicketFilters = !NO_TICKET_FILTERS.includes(name);
  const activeCount = Object.values(apiFilters).filter(Boolean).length;

  async function runExport(format: ExportFormat) {
    setExportError(null);
    try {
      // Export exactly the columns currently on screen when some are hidden.
      const partial = visibleCols.length > 0 && visibleCols.length < columns.length;
      await exportReport.mutateAsync({
        name,
        format,
        filters: apiFilters,
        ...(partial ? { cols: visibleCols } : {}),
      });
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : "Export failed.");
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Reports</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Operational and financial reports — filter, choose columns, and export to CSV, Excel or PDF.
        </p>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-border" role="tablist" aria-label="Reports">
        {reports.map((r) => (
          <button
            key={r.id}
            type="button"
            role="tab"
            aria-selected={name === r.id}
            onClick={() => setName(r.id)}
            className={
              "shrink-0 border-b-2 px-4 py-2 text-sm font-medium " +
              (name === r.id
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground")
            }
          >
            {r.label}
          </button>
        ))}
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        showTicketFilters={showTicketFilters}
        activeCount={activeCount}
      />

      <div className="flex flex-wrap items-center gap-2">
        {columns.length > 0 && <ColumnChooser columns={columns} hidden={hidden} onToggle={toggleColumn} />}
        <div className="ml-auto flex items-center gap-2">
          <RequirePermission permission="report:export">
            {(["csv", "xlsx", "pdf"] as ExportFormat[]).map((f) => (
              <Button
                key={f}
                variant="outline"
                disabled={exportReport.isPending || columns.length === 0}
                onClick={() => void runExport(f)}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {f.toUpperCase()}
              </Button>
            ))}
          </RequirePermission>
        </div>
      </div>

      {exportError && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {exportError}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              {visibleCols.map((i) => (
                <th key={i} className="px-4 py-2.5 font-medium">
                  {columns[i]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={Math.max(1, visibleCols.length)} className="px-4 py-8 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={Math.max(1, visibleCols.length)} className="px-4 py-8 text-center text-destructive">
                  {error instanceof ApiError ? error.message : "Could not load the report."}
                </td>
              </tr>
            )}
            {data?.rows.length === 0 && (
              <tr>
                <td colSpan={Math.max(1, visibleCols.length)} className="px-4 py-8 text-center text-muted-foreground">
                  No data for this selection.
                </td>
              </tr>
            )}
            {data?.rows.map((row, i) => (
              <tr
                key={i}
                className={
                  "border-t border-border " +
                  (row[0] === "TOTAL" ? "bg-muted/40 font-semibold" : "")
                }
              >
                {visibleCols.map((j) => (
                  <td key={j} className="px-4 py-2.5 tabular-nums">
                    {row[j] ?? "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {name === "revenue" && canRevenue && <BillingRatesPanel />}
    </div>
  );
}

function FilterBar({
  filters,
  onChange,
  showTicketFilters,
  activeCount,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  showTicketFilters: boolean;
  activeCount: number;
}) {
  const { data: sites } = useSites();
  const { data: customers } = useCustomers({ page_size: 100 });
  const { data: users } = useUsers({ page_size: 100, is_active: true });
  const { data: categories } = useTicketCategories();

  const set = (patch: Partial<Filters>) => onChange({ ...filters, ...patch });
  const engineers = (users?.items ?? []).filter((u) => u.role_code !== "customer");

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
        Filters
        {activeCount > 0 && <Badge tone="success">{activeCount} active</Badge>}
        {activeCount > 0 && (
          <button
            type="button"
            onClick={() => onChange(EMPTY)}
            className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
            Clear all
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="From" htmlFor="r_start">
          <Input id="r_start" type="date" value={filters.start} onChange={(e) => set({ start: e.target.value })} />
        </Field>
        <Field label="To" htmlFor="r_end">
          <Input id="r_end" type="date" value={filters.end} onChange={(e) => set({ end: e.target.value })} />
        </Field>
        <Field label="Site" htmlFor="r_site">
          <Select id="r_site" value={filters.site} onChange={(e) => set({ site: e.target.value })}>
            <option value="">All sites</option>
            {(sites ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </Select>
        </Field>
        {showTicketFilters && (
          <>
            <Field label="Customer" htmlFor="r_customer">
              <Select id="r_customer" value={filters.customer_id} onChange={(e) => set({ customer_id: e.target.value })}>
                <option value="">All customers</option>
                {(customers?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Engineer" htmlFor="r_engineer">
              <Select id="r_engineer" value={filters.engineer_id} onChange={(e) => set({ engineer_id: e.target.value })}>
                <option value="">All engineers</option>
                {engineers.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </Select>
            </Field>
            <Field label="Status" htmlFor="r_status">
              <Select id="r_status" value={filters.status} onChange={(e) => set({ status: e.target.value })}>
                <option value="">Any status</option>
                {(Object.keys(STATUS_LABELS) as TicketStatus[]).map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </Select>
            </Field>
            <Field label="Priority" htmlFor="r_priority">
              <Select id="r_priority" value={filters.priority} onChange={(e) => set({ priority: e.target.value })}>
                <option value="">Any priority</option>
                {(Object.keys(PRIORITY_LABELS) as TicketPriority[]).map((p) => (
                  <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                ))}
              </Select>
            </Field>
            <Field label="Category" htmlFor="r_category">
              <Select id="r_category" value={filters.category_id} onChange={(e) => set({ category_id: e.target.value })}>
                <option value="">All categories</option>
                {(categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </Field>
          </>
        )}
      </div>
    </div>
  );
}

function ColumnChooser({
  columns,
  hidden,
  onToggle,
}: {
  columns: string[];
  hidden: number[];
  onToggle: (idx: number) => void;
}) {
  const shown = columns.length - hidden.length;
  return (
    <details className="group relative">
      <summary className="flex cursor-pointer list-none items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent">
        <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
        Columns
        <Badge tone="muted">{shown}/{columns.length}</Badge>
      </summary>
      <div className="absolute z-10 mt-1 w-56 rounded-md border border-border bg-card p-2 shadow-lg">
        {columns.map((c, i) => (
          <label
            key={i}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent"
          >
            <input
              type="checkbox"
              checked={!hidden.includes(i)}
              // The first column is the report's row label — keep it anchored.
              disabled={i === 0}
              onChange={() => onToggle(i)}
            />
            <span className={i === 0 ? "text-muted-foreground" : ""}>{c}</span>
          </label>
        ))}
      </div>
    </details>
  );
}

function BillingRatesPanel() {
  const { data: rates } = useBillingRates();
  const { data: customers } = useCustomers({ page_size: 100 });
  const create = useCreateBillingRate();
  const remove = useDeleteBillingRate();

  const [customerId, setCustomerId] = useState("");
  const [rate, setRate] = useState("");
  const [error, setError] = useState<string | null>(null);

  const customerName = (id: string | null) =>
    id === null ? "Global default" : customers?.items.find((c) => c.id === id)?.company_name ?? id;

  async function add() {
    setError(null);
    if (!rate) return;
    try {
      await create.mutateAsync({
        customer_id: customerId || null,
        label: "Standard",
        hourly_rate: rate,
      });
      setRate("");
      setCustomerId("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add the rate.");
    }
  }

  return (
    <section className="rounded-lg border border-border p-5">
      <h2 className="text-sm font-medium">Billing rates</h2>
      <p className="mb-3 mt-0.5 text-xs text-muted-foreground">
        Hourly charge applied to billable engineer time. A customer without a rate uses the global default.
      </p>

      {error && (
        <div role="alert" className="mb-2 rounded-md bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
          {error}
        </div>
      )}

      <div className="mb-3 space-y-1.5">
        {(rates ?? []).map((r) => (
          <div key={r.id} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
            {r.customer_id === null ? (
              <Badge tone="muted">Global default</Badge>
            ) : (
              <span>{customerName(r.customer_id)}</span>
            )}
            <span className="ml-auto font-medium tabular-nums">₹{r.hourly_rate}/hr</span>
            <ConfirmButton
              aria-label={`Delete rate for ${customerName(r.customer_id)}`}
              title="Delete billing rate?"
              message={<>Delete the billing rate for <span className="font-medium text-foreground">{customerName(r.customer_id)}</span>?</>}
              onConfirm={async () => {
                await remove.mutateAsync(r.id);
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </ConfirmButton>
          </div>
        ))}
        {(rates ?? []).length === 0 && (
          <p className="text-xs text-muted-foreground">No rates yet — add the global default below.</p>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[180px] flex-1">
          <Select aria-label="Customer for rate" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Global default</option>
            {(customers?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.company_name}
              </option>
            ))}
          </Select>
        </div>
        <Input
          aria-label="Hourly rate"
          type="number"
          min="0"
          step="0.01"
          className="w-32"
          placeholder="₹ / hour"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
        />
        <Button variant="outline" disabled={!rate || create.isPending} onClick={() => void add()}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add rate
        </Button>
      </div>
    </section>
  );
}
